import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {Label} from '@primer/react'
import {type FC, type ReactNode, useMemo} from 'react'
import styles from './FindingCommentPreview.module.css'
import type {CommentPreviewData} from './types'

interface FindingCommentPreviewProps {
  preview: CommentPreviewData
  filePath?: string
  suggestionAction?: ReactNode
  suggestionApplied?: boolean
}

export const FindingCommentPreview: FC<FindingCommentPreviewProps> = ({
  preview,
  filePath,
  suggestionAction,
  suggestionApplied = false,
}) => {
  const {bodyHTML, diffHunk, suggestion} = preview

  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side
  const safeBodyHTML = bodyHTML as SafeHTMLString
  const suggestionDiffEntries = suggestion?.diffEntries ?? []
  const suggestionState = suggestionApplied ? 'applied' : suggestion?.state
  const showDiff = suggestionDiffEntries.length === 0
  const diffHunkLines = useMemo(() => (showDiff && diffHunk ? parseUnifiedDiff(diffHunk) : []), [showDiff, diffHunk])

  return (
    <div className="d-flex flex-column gap-2" data-testid="finding-comment-preview">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <VerifiedHTMLDiv html={safeBodyHTML} className="markdown-body timeline-comment comment-body tmp-p-3 tmp-my-2" />
      {diffHunkLines.length > 0 && (
        <div data-testid="finding-comment-diff-hunk">
          <div className="d-flex flex-column overflow-hidden border rounded-2">
            {filePath && (
              <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                <span>{filePath}</span>
              </div>
            )}
            <StaticUnifiedDiffPreview
              tabSize={4}
              className={styles.diffPreview}
              diffTableClassName="borderColor-default"
              diffLines={diffHunkLines}
            />
          </div>
        </div>
      )}

      {suggestionDiffEntries.length > 0 && (
        <div className="d-flex flex-column gap-3" data-testid="finding-comment-suggestion">
          <div className="d-flex flex-items-center flex-justify-between flex-wrap gap-2">
            <div className="d-flex flex-items-center gap-2">
              <h3 className="text-small text-semibold color-fg-muted m-0">Suggested change</h3>
              {suggestionState === 'applied' && (
                <Label variant="done" size="small">
                  Applied
                </Label>
              )}
              {suggestionState === 'outdated' && (
                <Label variant="attention" size="small">
                  Outdated
                </Label>
              )}
            </div>
            {suggestionAction}
          </div>
          {suggestionDiffEntries.map(entry => {
            const key = `${entry.path}-${entry.diffLines
              .map(line => `${line.left ?? ''}:${line.right ?? ''}:${line.type}:${line.text}`)
              .join('|')}`

            return (
              <div key={key}>
                <div className="d-flex flex-column overflow-hidden border rounded-2">
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                    <span className="wb-break-word">{entry.path}</span>
                  </div>
                  <StaticUnifiedDiffPreview
                    tabSize={4}
                    className={styles.diffPreview}
                    diffTableClassName="borderColor-default"
                    diffLines={entry.diffLines}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
